import React, {useEffect, useState} from 'react';
import {Box, Button, Collapse, Grid, Link, Typography, useTheme} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { get } from 'lodash';
import CustomerPopupModal from "../../components/form/CustomerPopupModal";

const ProfileCard = ({ title, subtitle, document, rawData, onUpdate, readOnly, invoiceType }) => {
  const theme = useTheme();
    const [detailsOpen, setDetailsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [summaryParts, setSummaryParts] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const formatAddress = (prefix) => {
    const parts = [
      get(document, `${prefix}street_name`),
      get(document, `${prefix}additional_street_name`),
      get(document, `${prefix}address_line`),
      get(document, `${prefix}city_name`),
      get(document, `${prefix}country_subentity`),
      get(document, `${prefix}postal_zone`),
    ]?.filter(Boolean);
    return parts.join(', ');
  };

    const presentValue = (value) => {
        if (value == null || String(value).trim() === '') {
            return null;
        }

        return String(value).trim();
    };


    const vatLabel = document?.party_tax_scheme?.tax_scheme ;
    const businessName = presentValue(get(document, 'party_name')) || presentValue(get(document, 'name'));
    const vatValue = presentValue(get(document, 'party_tax_scheme.company_id'));
    const regNumber = presentValue(get(document, 'company_id'));
    const email = presentValue(get(document, 'email'));
    const countryCode = presentValue(get(document, 'postal_address.country_code'));

    useEffect(() => {
        if(get(document, 'type') === "INDIVIDUAL"){
            if(get(document, 'postal_address.country_code') === "OM"){
                setSummaryParts(_ => [ businessName, regNumber]);
            }
            else {
                setSummaryParts(_ => [businessName, countryCode])
            }
        }
        else{
            if(get(document, 'postal_address.country_code') === "OM"){
                setSummaryParts(_ => [ businessName, vatValue, regNumber]);
            }
            else{
                setSummaryParts(_ => [businessName, countryCode, regNumber])
            }
        }
    }, [document, businessName, vatValue, email, regNumber, countryCode]);

    const formatPeppolId = () => {
        const schemeId = document?.endpoint_scheme_id;
        const endpointId = document?.endpoint_id;

        if (schemeId == null || String(schemeId).trim() === '' || endpointId == null || String(endpointId).trim() === '') {
            return '-';
        }

        return `${String(schemeId).trim()}:${String(endpointId).trim()}`;
    };


    const fields = [
    { id: 'contactPerson', label: 'Contact Person', key: 'name' },
    { id: 'businessName', label: 'Business Name', key: 'party_name' },
    {  id: 'address',label: 'Address', key: 'postal_address' },
    { id: 'country', label: 'Country', key: 'postal_address.country_code' },
    { id: 'phone', label: 'Phone', key: 'telephone' },
    { id: 'email',label: 'Email', key: 'email' },
    ...(document?.party_tax_scheme?.tax_scheme
      ? [{ id: 'vat',label: vatLabel, key: 'party_tax_scheme.company_id' }]
      : []),
    ...(document?.scheme_agency_id ? [{ id: 'tinTrn', label: document.scheme_agency_id, key: 'company_id' }] : []),
      { id: 'peppolId', label: "Peppol ID", getValue: formatPeppolId },
  ];

  return (
      <>
          <Box
              sx={{
                  px: 1.5,
                  py: 1.25,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: 'inherit',
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
              }}
          >
              <Box sx={{ mb: 0.75 }}>
                  <Typography
                      variant="body1"
                      sx={{
                          fontWeight: 600,
                          lineHeight: 1.3,
                          fontSize: '1rem',
                      }}
                  >
                      {subtitle || title}
                  </Typography>
              </Box>

              <Box
                  sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      columnGap: 1.5,
                      rowGap: 0.75,
                  }}
              >
                  {summaryParts.map((part, index) => (
                      <React.Fragment key={`${part}-${index}`}>
                          {index > 0 && part!== null ? (
                              <Box
                                  aria-hidden
                                  sx={{
                                      alignSelf: 'stretch',
                                      width: '2px',
                                      backgroundColor: 'text.primary',
                                      opacity: 0.35,
                                  }}
                              />
                          ) : null}
                          <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                  lineHeight: 1,
                                  wordBreak: 'break-word',
                              }}
                          >
                              {part}
                          </Typography>
                      </React.Fragment>
                  ))}
              </Box>

              <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => setDetailsOpen((prev) => !prev)}
                  sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      gap: 0.25,
                      mt: 0.75,
                      color: 'primary.main',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      p: 0,
                      font: 'inherit',
                  }}
              >
                  {detailsOpen ? ('Hide details') : ('View details')}
                  {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Link>

              <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                  <Box
                      sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: theme.spacing(1.25),
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                  >
                      {fields.map((field) => (
                          <Grid container key={field.id || field.label}>
                              <Grid fontSize="0.90rem" size={{ xs: 4 }} sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {field.label}:
                              </Grid>
                              <Grid
                                  size={{ xs: 5 }}
                                  fontSize="0.90rem"
                                  sx={{
                                      maxWidth: 300,
                                      wordWrap: 'break-word',
                                      marginLeft: theme.spacing(1),
                                      fontWeight: 400,
                                      color: 'text.secondary',
                                  }}
                                  style={
                                      field.id === 'address'
                                          ? {
                                              direction: 'ltr',
                                              unicodeBidi: 'isolate',
                                              textAlign: 'left',
                                          }
                                          : undefined
                                  }
                              >
                                  {typeof field.getValue === 'function'
                                      ? field.getValue()
                                      : field.id === 'address'
                                          ? formatAddress(field.key ? `${field.key}.` : '') || '-'
                                          : String(get(document, field.key || 'defaultKey') || '-')}
                              </Grid>
                          </Grid>
                      ))}
                  </Box>
              </Collapse>

              <Box sx={{ flex: 1 }} />
          </Box>
          {open && (
              <CustomerPopupModal
                  open={open}
                  handleClose={handleClose}
                  document={document}
                  onSave={onUpdate}
                  setError={() => {}}
              />
          )}
      </>
  );
};

export default ProfileCard;
